<script lang="ts" setup>
import {useCartStore} from "@/stores/cart";
import type {TProduct} from "@/components/cart/CartPage.vue";
import {price} from "@/filters/price";
import CartInput from "@/components/cart/CartInput.vue";
import {useModalsStore} from "@/stores/modals";

const props = withDefaults(defineProps<{ product: TProduct, favorite?: boolean }>(), {
  favorite: false
})

const cartStore = useCartStore()
const modalStore = useModalsStore()

const removeAction = (productId: number) => {
  if (props.favorite) {
    cartStore.addToFavorite(productId)

    return
  }

  cartStore.removeFromCart(productId)
}
</script>

<template>
  <tr class="max-desktop:flex max-desktop:p-2.5 gap-4 flex-col items-start max-desktop:border max-desktop:bg-[#F7F7F7]">
    <td class="desktop:border p-5 max-desktop:hidden">
      <svg class="transition-colors hover:text-[hsl(var(--roke-text-blue))] cursor-pointer" fill="none" height="20"
           viewBox="0 0 21 20" width="21" xmlns="http://www.w3.org/2000/svg" @click="removeAction(+props.product.id)">
        <path
          d="M0.689743 0.391988L0.783446 0.283446C1.12702 -0.0601288 1.66465 -0.0913545 2.0435 0.189743L2.15204 0.283446L10.5 8.63097L18.848 0.283446C19.2259 -0.0944772 19.8386 -0.0944772 20.2165 0.283446C20.5945 0.661369 20.5945 1.27412 20.2165 1.65204L11.869 10L20.2165 18.348C20.5601 18.6915 20.5914 19.2292 20.3102 19.608L20.2165 19.7165C19.873 20.0601 19.3354 20.0914 18.9565 19.8102L18.848 19.7165L10.5 11.369L2.15204 19.7165C1.77412 20.0945 1.16137 20.0945 0.783446 19.7165C0.405523 19.3386 0.405523 18.7259 0.783446 18.348L9.13097 10L0.783446 1.65204C0.439871 1.30847 0.408646 0.77084 0.689743 0.391988Z"
          fill="currentColor"/>
      </svg>
    </td>
    <td class="desktop:border desktop:p-5 max-desktop:text-left">
      <a :href="props.product.url"
         class="transition-colors hover:text-[hsl(var(--roke-text-blue))] cursor-pointer text-[24px] leading-none">{{
          props.product.title
        }}</a>
    </td>
    <td class="desktop:border desktop:p-5 text-xl max-desktop:-order-1">
      <span class="max-desktop:block max-desktop:text-[16px] max-desktop:leading-[16px]">
        <span class="text-base leading-none desktop:text-lg text-[hsl(var(--roke-text-grey))]">Артикул:</span>
        <span class="ml-1 max-desktop:font-semibold max-desktop:text-base max-desktop:leading-[14.4px]">{{
            props.product.sku
          }}</span>
      </span>
    </td>
    <td class="desktop:border max-desktop:hidden">
      <textarea class="outline-none text-[18px] leading-[16.2px] h-full resize-none p-2.5 mt-2 text-left"
                placeholder="Введите комментарий к товару"
                @input="cartStore.addComment({ id: +props.product.id, comment: ($event.target as HTMLInputElement).value })"
                v-html="cartStore.getComment(+props.product.id)"></textarea>
      <textarea class="outline-none text-[18px] leading-[16.2px] h-full resize-none p-2.5 !mt-0 text-left" readonly
                v-html="cartStore.getAnalog(+props.product.id)"></textarea>
    </td>
    <td v-if="props.product.price" class="desktop:border desktop:p-5 max-desktop:mt-[30px]">
      <span class="max-desktop:block max-desktop:text-[16px] max-desktop:leading-[16px]">
        <span class="text-base leading-none desktop:hidden text-[hsl(var(--roke-text-grey))]">Цена:</span>
        <span class="max-desktop:ml-1 max-desktop:font-semibold max-desktop:text-base max-desktop:leading-[14.4px]">{{
            price(props.product.price as string)
          }}</span>
      </span>
    </td>
    <td class="max-desktop:hidden desktop:border desktop:p-5">
      <CartInput v-if="!props.favorite" :price="props.product.price" :product-id="+props.product.id" class="!w-40"/>
      <template v-else>
        <CartInput v-if="cartStore.isItemExists(props.product.id)" :price="props.product.price"
                   :product-id="props.product.id" class="max-desktop:h-11 !w-40 desktop:h-9 m-auto"/>
        <button v-else
                class="w-full desktop:!w-40 rounded-[3px] h-11 desktop:h-9 inline-flex items-center whitespace-nowrap ring-offset-background transition-colors disabled:pointer-events-none disabled:opacity-50 bg-[hsl(var(--roke-text-blue))] text-white justify-center uppercase px-6 font-semibold text-xl leading-[16.2px]"
                @click="cartStore.addToCart(props.product.id, props.product.price)">
          <span class="mt-0.5">В корзину</span>
        </button>
      </template>
    </td>
    <td v-if="!props.favorite && props.product.price" class="desktop:border max-desktop:hidden desktop:p-5">
      {{ price(cartStore.getItemTotalSum(props.product.id) as number) }}
    </td>
    <td class="desktop:hidden max-desktop:flex max-desktop:w-full max-desktop:-mt-2.5">
      <div class="w-full flex flex-row justify-center gap-2.5 items-center">
        <CartInput v-if="cartStore.isItemExists(props.product.id)" :price="props.product.price"
                   :product-id="props.product.id" class="max-desktop:h-11 desktop:w-1/2"/>
        <button v-else
                class="max-desktop:w-full desktop:w-1/2 rounded-[3px] h-11 desktop:h-9 inline-flex items-center whitespace-nowrap ring-offset-background transition-colors disabled:pointer-events-none disabled:opacity-50 bg-[hsl(var(--roke-text-blue))] text-white justify-center uppercase px-6 font-semibold text-xl leading-[16.2px]"
                @click="cartStore.addToCart(props.product.id, props.product.price)">
          <span class="mt-0.5">В корзину</span>
        </button>
        <button aria-expanded="false"
                class="size-11 bg-white rounded-[3px] flex-shrink-0 flex outline-none desktop:hidden" type="button"
                @click="modalStore.showCommentModal(props.product.id)">
          <div class="relative size-7 m-auto">
            <span class="block size-7 group" data-nimg="1">
              <img alt="comment" class="transition-opacity bg-clip-content" data-nimg="1" decoding="async" height="32"
                   loading="lazy" src="/icons/comment_hover.svg" width="32">
            </span>
            <div v-if="cartStore.isCommentExists(props.product.id)"
                 class="size-3 rounded-full bg-[#3064C7] absolute -bottom-0.5 -right-0.5"></div>
          </div>
        </button>
        <div class="bg-white h-full flex" @click="cartStore.addToFavorite(props.product.id)">
          <div
            class="text-[#3064C7] size-11 desktop:size-9 desktop:bg-[#F7F7F7] flex cursor-pointer group m-auto rounded-[3px]">
            <div class="m-auto relative size-7 desktop:size-4">
              <svg
                :class="cartStore.isFavorite(props.product.id) ? 'opacity-0 desktop:group-hover:opacity-100' : 'opacity-100 desktop:group-hover:opacity-0'"
                class="m-auto transition-opacity absolute top-0 size-7 desktop:size-4" fill="none" height="16"
                viewBox="0 0 18 16" width="16" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M8.63895 2.56888L8.99779 2.93783L9.3562 2.56848L10.0943 1.80786C10.0944 1.80781 10.0944 1.80777 10.0944 1.80773C11.7904 0.064048 14.5299 0.0640894 16.2258 1.80785C17.9253 3.5554 17.9239 6.38663 16.2267 8.14207C16.2265 8.14229 16.2263 8.1425 16.2261 8.14271L9.11721 15.448L9.11711 15.4482C9.04988 15.5173 8.94677 15.5173 8.87954 15.4482L1.77505 8.14311C0.074981 6.39501 0.0749858 3.55523 1.77505 1.80717L1.77505 1.80717C3.46753 0.0668963 6.20567 0.0669012 7.89811 1.80717L7.89812 1.80718L8.63895 2.56888Z"
                  fill="none" stroke="currentColor"/>
              </svg>
              <svg
                :class="cartStore.isFavorite(props.product.id) ? 'opacity-100 desktop:group-hover:opacity-100' : 'opacity-0 desktop:group-hover:opacity-100'"
                class="m-auto transition-opacity absolute top-0 size-7 desktop:size-4" fill="none" height="16"
                viewBox="0 0 18 16" width="16" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M8.63895 2.56888L8.99779 2.93783L9.3562 2.56848L10.0943 1.80786C10.0944 1.80781 10.0944 1.80777 10.0944 1.80773C11.7904 0.064048 14.5299 0.0640894 16.2258 1.80785C17.9253 3.5554 17.9239 6.38663 16.2267 8.14207C16.2265 8.14229 16.2263 8.1425 16.2261 8.14271L9.11721 15.448L9.11711 15.4482C9.04988 15.5173 8.94677 15.5173 8.87954 15.4482L1.77505 8.14311C0.074981 6.39501 0.0749858 3.55523 1.77505 1.80717L1.77505 1.80717C3.46753 0.0668963 6.20567 0.0669012 7.89811 1.80717L7.89812 1.80718L8.63895 2.56888Z"
                  fill="currentColor" stroke="currentColor"/>
              </svg>
            </div>
          </div>
        </div>
      </div>
    </td>
  </tr>
</template>
